import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { COMPLETED_TEXT, NOT_COMPLETED_TEXT, parseTaskStatus } from '../utils/taskStatus';
import { canRestartStage, STAGE_LABEL_KEY, STAGE_TAG_CLASS } from '../utils/taskStage';
import { formatMinutes } from '../utils/date';
import { PRIORITY_LABEL_KEY, PRIORITY_ORDER, PRIORITY_TAG_CLASS } from '../utils/priority';
import { PriorityLevel, TodoProgressKind, TodoStatusDto, TodoTaskDto, TodoTaskStage } from '../web-api-client';
import { CaretDownIcon, CheckIcon, GripIcon, RestartIcon, XIcon } from './icons';
import { TaskStatusPicker } from './TaskStatusPicker';
import { DragItemProps } from './useDragReorder';

/**
 * Danh sách công việc bên trong một todo.
 *
 * Mặc định mỗi dòng chỉ có năm thứ: huy hiệu vòng đời, tay cầm kéo, ô tick, mũi tên mở rộng, và nội
 * dung. Ba trường còn lại của một task — mức ưu tiên, thời lượng ước tính, bình luận — cùng thanh vòng
 * đời đầy đủ nằm sau mũi tên đó.
 *
 * Ô tick vẫn tick nhanh kết quả (result) như trước; huy hiệu vòng đời mới là một trục khác, độc lập —
 * task phải "bắt đầu" mới nhập được kết quả, và phải có kết quả mới "kết thúc" được, đúng luật máy chủ
 * quy định.
 */
interface ChecklistProps {
  tasks: TodoTaskDto[];
  status: TodoStatusDto | undefined;
  draggingIndex: number | null;
  itemProps: (index: number) => DragItemProps;
  onAdd: (content: string) => Promise<void>;
  onStatus: (index: number, status: string) => void;
  onStart: (index: number) => void;
  onFinish: (index: number) => void;
  onRestart: (index: number, reason: string) => Promise<boolean>;
  onSave: (task: TodoTaskDto, changes: Partial<TodoTaskDto>) => void;
  onComment: (index: number, comment: string) => void;
  onRemove: (task: TodoTaskDto) => void;
}

export function TaskChecklist({
  tasks,
  status,
  draggingIndex,
  itemProps,
  onAdd,
  onStatus,
  onStart,
  onFinish,
  onRestart,
  onSave,
  onComment,
  onRemove
}: ChecklistProps) {
  const { t } = useI18n();

  const total = status?.total ?? 0;
  const completed = status?.completed ?? 0;
  const partial = status?.partial ?? 0;
  const notDone = status?.notCompleted ?? 0;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  const allDone = total > 0 && completed === total;

  // Chỉ nhắc những con số khác 0. "0 dở dang" không nói lên điều gì ngoài việc chiếm chỗ.
  const extraCounts: string[] = [];
  if (partial > 0) extraCounts.push(t('todo.counts.partial', { count: partial }));
  if (notDone > 0) extraCounts.push(t('todo.counts.notCompleted', { count: notDone }));

  return (
    <section className="panel task-board">
      <header className="task-board-head">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${percent}%` }} />
        </div>

        <span className="task-board-count" data-testid="task-progress">
          <span data-testid="task-done">{completed}</span>/<span data-testid="task-total">{total}</span>
          {allDone && <CheckIcon size={16} className="text-success" />}
        </span>
      </header>

      {extraCounts.length > 0 && <p className="task-board-note">{extraCounts.join(' · ')}</p>}

      <QuickAdd onAdd={onAdd} />

      <ul className="checklist list-unstyled">
        {tasks.map((task, position) => (
          <ChecklistRow
            key={`${task.index}-${task.content}`}
            task={task}
            dragging={draggingIndex === position}
            dragProps={itemProps(position)}
            onStatus={value => onStatus(task.index!, value)}
            onStart={() => onStart(task.index!)}
            onFinish={() => onFinish(task.index!)}
            onRestart={reason => onRestart(task.index!, reason)}
            onSave={changes => onSave(task, changes)}
            onComment={value => onComment(task.index!, value)}
            onRemove={() => onRemove(task)}
          />
        ))}
      </ul>
    </section>
  );
}

/** Ô thêm nhanh nằm ngay đầu danh sách; hai nút xác nhận chỉ hiện khi đang gõ. */
function QuickAdd({ onAdd }: { onAdd: (content: string) => Promise<void> }) {
  const { t } = useI18n();

  const [content, setContent] = useState('');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    const trimmed = content.trim();

    if (!trimmed || busy) return;

    setBusy(true);

    try {
      await onAdd(trimmed);
      setContent('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setContent('');
    setActive(false);
    inputRef.current?.blur();
  };

  return (
    <div className="quick-add">
      <input
        ref={inputRef}
        className="form-control quick-add-input"
        placeholder={t('detail.addItem')}
        value={content}
        maxLength={200}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        onChange={event => setContent(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
          if (event.key === 'Escape') cancel();
        }}
        data-testid="new-task-content"
      />

      {(active || content) && (
        <div className="quick-add-actions">
          <button
            type="button"
            className="icon-action"
            onMouseDown={event => event.preventDefault()}
            onClick={submit}
            disabled={busy || !content.trim()}
            aria-label={t('day.add')}
            title={t('day.add')}
            data-testid="confirm-new-task"
          >
            <CheckIcon size={16} />
          </button>
          <button
            type="button"
            className="icon-action"
            onMouseDown={event => event.preventDefault()}
            onClick={cancel}
            aria-label={t('day.cancel')}
            title={t('day.cancel')}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

interface RowProps {
  task: TodoTaskDto;
  dragging: boolean;
  dragProps: DragItemProps;
  onStatus: (status: string) => void;
  onStart: () => void;
  onFinish: () => void;
  onRestart: (reason: string) => Promise<boolean>;
  onSave: (changes: Partial<TodoTaskDto>) => void;
  onComment: (comment: string) => void;
  onRemove: () => void;
}

function ChecklistRow({
  task,
  dragging,
  dragProps,
  onStatus,
  onStart,
  onFinish,
  onRestart,
  onSave,
  onComment,
  onRemove
}: RowProps) {
  const { t, lang } = useI18n();

  const progress = parseTaskStatus(task.result);
  const done = progress.kind === TodoProgressKind.Completed;
  const partial = progress.kind === TodoProgressKind.Partial;
  const stage = task.status ?? TodoTaskStage.New;

  const [open, setOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [content, setContent] = useState(task.content ?? '');
  const [comment, setComment] = useState(task.comment ?? '');
  const checkboxRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const stageTriggerRef = useRef<HTMLDivElement>(null);

  // Đóng popover vòng đời (mở từ icon tay cầm kéo) khi bấm ra ngoài.
  useEffect(() => {
    if (!stageOpen) return;

    const onClickOutside = (event: MouseEvent) => {
      if (stageTriggerRef.current && !stageTriggerRef.current.contains(event.target as Node)) {
        setStageOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [stageOpen]);

  // Sau mỗi lần lưu, dữ liệu được tải lại từ máy chủ; đồng bộ để ô nhập không giữ bản nháp cũ.
  useEffect(() => setContent(task.content ?? ''), [task.content]);
  useEffect(() => setComment(task.comment ?? ''), [task.comment]);

  // Dạng "hoàn thành x/y" không phải tick cũng không phải bỏ trống — nó là ô tick lửng.
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = partial;
  }, [partial]);

  // Textarea không tự cao ra theo nội dung — tự tính lại height mỗi khi chữ đổi, để chữ dài xuống
  // dòng thay vì bị cắt.
  useLayoutEffect(() => autoGrow(contentRef.current), [content]);
  useLayoutEffect(() => autoGrow(commentRef.current), [comment, open]);

  const commitContent = () => {
    const trimmed = content.trim();

    if (!trimmed || trimmed === task.content) {
      setContent(task.content ?? '');
      return;
    }

    onSave({ content: trimmed });
  };

  const priority = task.priority ?? PriorityLevel.None;

  // Huy hiệu đầu dòng chỉ để HIỂN THỊ giai đoạn hiện tại — bấm được đúng một trường hợp duy nhất:
  // đang New thì bấm để Bắt đầu. Chuyển sang Kết thúc chỉ đi qua thanh 3 bước trong khung mở rộng.
  const stageClickable = stage === TodoTaskStage.New;

  const confirmRemove = () => {
    if (window.confirm(t('detail.confirmDeleteTask', { content: task.content ?? '' }))) {
      onRemove();
    }
  };

  return (
    <li className={`check-item ${dragging ? 'is-dragging' : ''}`}>
      <div className="check-row" {...dragProps}>
        <button
          type="button"
          className={`stage-badge ${STAGE_TAG_CLASS[stage]} ${stageClickable ? '' : 'is-disabled'}`}
          disabled={!stageClickable}
          aria-label={t('taskStage.startAria')}
          onClick={onStart}
        >
          {t(STAGE_LABEL_KEY[stage])}
        </button>

        <div className="stage-trigger" ref={stageTriggerRef}>
          <button
            type="button"
            className="drag-handle drag-handle-btn"
            title={t('taskStatus.label')}
            aria-label={t('taskStatus.label')}
            onClick={() => setStageOpen(value => !value)}
          >
            <GripIcon />
          </button>

          {stageOpen && (
            <div className="stage-popover">
              <StageOptionsList stage={stage} result={task.result} onStart={onStart} onFinish={onFinish} />
            </div>
          )}
        </div>

        <input
          ref={checkboxRef}
          type="checkbox"
          className="form-check-input check-box"
          checked={done}
          onChange={event => onStatus(event.target.checked ? COMPLETED_TEXT : NOT_COMPLETED_TEXT)}
          aria-label={t('detail.toggleDone')}
          data-testid="task-checkbox"
        />

        <button
          type="button"
          className={`row-caret ${open ? 'is-open' : ''}`}
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          aria-label={open ? t('detail.hideDetails') : t('detail.showDetails')}
          title={open ? t('detail.hideDetails') : t('detail.showDetails')}
        >
          <CaretDownIcon />
        </button>

        <textarea
          ref={contentRef}
          className={`check-text ${done ? 'is-done' : ''}`}
          value={content}
          maxLength={200}
          rows={1}
          onChange={event => setContent(event.target.value)}
          onBlur={commitContent}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') setContent(task.content ?? '');
          }}
          aria-label={t('detail.taskContent')}
        />

        <div className="check-marks">
          {partial && <span className="tag tag-warn">{progress.done}/{progress.total}</span>}
          {priority !== PriorityLevel.None && (
            <span className={PRIORITY_TAG_CLASS[priority]}>{t(PRIORITY_LABEL_KEY[priority])}</span>
          )}
          {!!task.estimateTime && (
            <span className="text-body-secondary small">{formatMinutes(task.estimateTime, lang)}</span>
          )}
        </div>

        <RestartControl stage={stage} onRestart={onRestart} />

        <button
          type="button"
          className="row-delete"
          onClick={confirmRemove}
          aria-label={t('detail.deleteTask')}
          title={t('detail.deleteTask')}
        >
          <XIcon size={16} />
        </button>
      </div>

      <div className={`check-detail-wrap ${open ? 'is-open' : ''}`}>
        <div className="check-detail-inner">
          <div className="check-detail">
            <div className="check-detail-row">
              <StageStepper stage={stage} result={task.result} onStart={onStart} onFinish={onFinish} />

              <TaskStatusPicker value={task.result} onChange={onStatus} />

              <select
                className="form-select form-select-sm w-auto"
                value={priority}
                onChange={event => onSave({ priority: event.target.value as PriorityLevel })}
                aria-label={t('priority.label')}
              >
                {PRIORITY_ORDER.map(level => (
                  <option key={level} value={level}>
                    {t(PRIORITY_LABEL_KEY[level])}
                  </option>
                ))}
              </select>

              <div className="input-group input-group-sm w-auto">
                <input
                  type="number"
                  min={0}
                  className="form-control estimate-input"
                  defaultValue={task.estimateTime ?? 0}
                  onBlur={event => {
                    const minutes = Math.max(0, Number(event.target.value) || 0);
                    if (minutes !== (task.estimateTime ?? 0)) onSave({ estimateTime: minutes });
                  }}
                  aria-label={t('detail.estimate')}
                />
                <span className="input-group-text">{t('targetTime.minutes')}</span>
              </div>
            </div>

            <textarea
              ref={commentRef}
              className="form-control form-control-sm mt-2 comment-input"
              rows={2}
              maxLength={2000}
              placeholder={t('detail.commentPlaceholder')}
              value={comment}
              onChange={event => setComment(event.target.value)}
              onBlur={() => {
                if (comment !== (task.comment ?? '')) onComment(comment);
              }}
            />
          </div>
        </div>
      </div>
    </li>
  );
}

/** Tự tính lại chiều cao theo nội dung — 'auto' trước để đo đúng scrollHeight, rồi ghim lại. */
function autoGrow(el: HTMLTextAreaElement | null) {
  if (!el) return;

  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

/**
 * Danh sách đủ 3 trạng thái hiện cùng lúc, trạng thái hiện tại được tô sáng — dùng trong popover mở từ
 * icon tay cầm kéo, nơi mục đích là xem nhanh cả vòng đời chứ không phải một ô chọn gọn.
 */
function StageOptionsList({
  stage,
  result,
  onStart,
  onFinish
}: {
  stage: TodoTaskStage;
  result: string | undefined;
  onStart: () => void;
  onFinish: () => void;
}) {
  const { t } = useI18n();

  const order = [TodoTaskStage.New, TodoTaskStage.InProcess, TodoTaskStage.End];
  const currentIndex = order.indexOf(stage);
  const canReachEnd = stage === TodoTaskStage.End || (stage === TodoTaskStage.InProcess && !!result);

  return (
    <div className="stage-stepper" role="group" aria-label={t('taskStatus.label')}>
      {order.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isPast = index < currentIndex;
        const clickable =
          (step === TodoTaskStage.InProcess && stage === TodoTaskStage.New) ||
          (step === TodoTaskStage.End && canReachEnd && stage !== TodoTaskStage.End);

        return (
          <React.Fragment key={step}>
            {index > 0 && <span className="stage-stepper-connector" aria-hidden="true" />}
            <button
              type="button"
              className={`stage-step ${isCurrent ? 'is-current' : ''} ${isPast ? 'is-past' : ''}`}
              disabled={!clickable}
              onClick={() => (step === TodoTaskStage.InProcess ? onStart() : onFinish())}
            >
              {t(STAGE_LABEL_KEY[step])}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Một dropdown như mọi ô chọn khác (ưu tiên, kết quả) thay vì thanh 3 nút riêng — dùng trong khung mở
 * rộng, nơi đã có sẵn nhiều ô chọn khác cùng hàng. Vẫn khóa đúng luật vòng đời: chỉ "New" hiện lên khi
 * đang New (không lùi được qua đây), chỉ "InProcess" bấm được khi đang New (để Start) hoặc đã đang
 * InProcess, chỉ "End" bấm được khi đang InProcess và đã có kết quả, hoặc đã đang End.
 */
function StageStepper({
  stage,
  result,
  onStart,
  onFinish
}: {
  stage: TodoTaskStage;
  result: string | undefined;
  onStart: () => void;
  onFinish: () => void;
}) {
  const { t } = useI18n();

  const canReachEnd = stage === TodoTaskStage.End || (stage === TodoTaskStage.InProcess && !!result);

  return (
    <select
      className="form-select form-select-sm w-auto"
      value={stage}
      aria-label={t('taskStatus.label')}
      onChange={event => {
        const next = event.target.value as TodoTaskStage;

        if (next === TodoTaskStage.InProcess && stage === TodoTaskStage.New) onStart();
        else if (next === TodoTaskStage.End && canReachEnd) onFinish();
      }}
    >
      <option value={TodoTaskStage.New} disabled={stage !== TodoTaskStage.New}>
        {t(STAGE_LABEL_KEY[TodoTaskStage.New])}
      </option>
      <option value={TodoTaskStage.InProcess} disabled={stage === TodoTaskStage.End}>
        {t(STAGE_LABEL_KEY[TodoTaskStage.InProcess])}
      </option>
      <option value={TodoTaskStage.End} disabled={!canReachEnd}>
        {t(STAGE_LABEL_KEY[TodoTaskStage.End])}
      </option>
    </select>
  );
}

/** Nút bắt đầu lại + popover nhỏ hỏi lý do, đóng khi bấm ra ngoài. */
function RestartControl({
  stage,
  onRestart
}: {
  stage: TodoTaskStage;
  onRestart: (reason: string) => Promise<boolean>;
}) {
  const { t } = useI18n();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const canRestart = canRestartStage(stage);

  const submit = async () => {
    const trimmed = reason.trim();

    if (!trimmed) {
      setError(t('taskStage.restartReasonRequired'));
      return;
    }

    setBusy(true);

    const ok = await onRestart(trimmed);

    setBusy(false);

    if (ok) {
      setOpen(false);
      setReason('');
      setError(null);
    }
  };

  return (
    <div className="restart-control" ref={wrapRef}>
      <button
        type="button"
        className="icon-action"
        disabled={!canRestart}
        aria-label={t('taskStage.restart')}
        title={t('taskStage.restart')}
        onClick={() => setOpen(value => !value)}
      >
        <RestartIcon size={16} />
      </button>

      {open && (
        <div className="restart-popover">
          <label className="restart-popover-label">{t('taskStage.restartReasonLabel')}</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            autoFocus
            value={reason}
            maxLength={500}
            placeholder={t('taskStage.restartReasonPlaceholder')}
            onChange={event => {
              setReason(event.target.value);
              setError(null);
            }}
          />

          {error && <p className="restart-popover-error">{error}</p>}

          <div className="restart-popover-actions">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(false)}>
              {t('taskStage.restartCancel')}
            </button>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={submit}>
              {t('taskStage.restartConfirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
